/**
 * anexosObrigatorios
 * --------------------------------------------------------------
 * Helpers compartilhados entre as páginas TCA, TcaEmpregador e TCE
 * para implementar o novo contrato de anexos (Tarefa A do Trello).
 *
 * Regra de negócio (front):
 *   - Os IDs dos anexos obrigatórios são definidos pelo FRONT, por
 *     Tipo de Programa, em `schema` (ANEXOS_SCHEMA_TCA_COMUM,
 *     ANEXOS_SCHEMA_TCA_EMPREGADOR, ANEXOS_SCHEMA_TCE).
 *   - Apenas o ESTUDANTE precisa enviar a lista
 *     `anexos_obrigatorios_ids` no body. Empresa/SEOP/SECEX/INFOR
 *     passam direto.
 *   - Quando o backend retorna 400 com `anexos_obrigatorios`, o front
 *     destaca visualmente os cards correspondentes.
 */

/**
 * Retorna a lista de IDs de anexo obrigatórios para o schema informado.
 * Aceita tanto `isStudent` (boolean) quanto `isEstudante` (boolean).
 * Se o usuário não for estudante, retorna lista vazia.
 *
 * Aceita `idSuffix` (string, opcional) que é concatenado a cada `id`
 * — usado para casar com as colunas do banco e o que o backend valida
 * no body. Ex.: `idSuffix: "_path"` faz o id virar `anexo_*_path`.
 */
export function buildAnexosObrigatoriosIds({
  schema,
  isEstudante,
  isStudent,
  idSuffix = "",
} = {}) {
  const isSt = isEstudante ?? isStudent ?? false;
  if (!isSt || !Array.isArray(schema)) return [];
  return schema
    .filter((doc) => doc && doc.required)
    .map((doc) => `${doc.id}${idSuffix}`);
}

/**
 * A partir do `anexosFiles` (state do front) e do schema, monta o
 * patch com as chaves de path/nome para o body do POST/PUT do contrato.
 *
 * Devolve várias variações de nomes para maximizar a chance de o
 * validator do back casar (o back historicamente variou):
 *   - `anexo_*_path`           (sufixo configurável, default)
 *   - `anexo_*_filename`
 *   - `anexo_*_file`
 *   - `anexo_*`                (sem sufixo)
 *   - `anexo_*_nome`           (rótulo do arquivo)
 *   - `anexo_*_originalname`
 *
 * Se o anexo não foi enviado (ou ainda está uploading), não inclui
 * nada (deixa o backend sinalizar o faltante via 400).
 */
export function buildAnexosObrigatoriosBodyPatch({
  schema,
  anexosFiles,
  idSuffix = "_path",
  nomeSuffix = "_nome",
}) {
  if (!Array.isArray(schema)) return {};
  const patch = {};
  schema.forEach((doc) => {
    if (!doc || !doc.id) return;
    const file = anexosFiles?.[doc.id];
    if (!file) return;

    // Resolve filename — usado em várias variações de chave.
    // 1) Tenta `file.filename` (estado após upload persistido).
    // 2) Tenta `file._file?.name` (File guardado dentro do entry).
    // 3) Fallback: se a entry for o próprio `File`, usa `file.name`.
    let filename = file.filename;
    let originalname = file.originalname || file.name || null;
    if (!filename && file.file && file.file.name) {
      filename = file.file.name;
      originalname = originalname || file.file.name;
    }
    if (!filename && file._file && file._file.name) {
      filename = file._file.name;
      originalname = originalname || file._file.name;
    }
    // Se a própria entry for um File (instância direta de File),
    // usa `name` como fallback para não mandar string vazia.
    const isFileInstance =
      typeof File !== "undefined" && file instanceof File;
    if (!filename && isFileInstance && file.name) {
      filename = file.name;
      originalname = file.name;
    }

    if (filename) {
      // Caminho físico do arquivo (esperado pelo back).
      patch[`${doc.id}${idSuffix}`] = filename; // anexo_*_path
      patch[`${doc.id}_filename`] = filename;
      patch[`${doc.id}_file`] = filename;
      patch[doc.id] = filename; // anexo_*
      // Também com prefixo `anexo_` para backends que esperam assim.
      patch[`anexo_${doc.id}${idSuffix}`] = filename;
      patch[`anexo_${doc.id}_filename`] = filename;
      patch[`anexo_${doc.id}`] = filename;

      // === Variações extras com `idColumn` (se definido) ===
      // Alguns anexos têm `id` amigável no front (ex.: "carga_horaria")
      // mas o nome da coluna no banco é diferente (ex.:
      // "declaracao_carga_horaria"). Sem isso, o PUT grava null na
      // coluna real. Quando o schema declara `idColumn`, espelhamos
      // TODAS as variações acima usando esse nome também.
      if (doc.idColumn && doc.idColumn !== doc.id) {
        patch[`${doc.idColumn}${idSuffix}`] = filename;
        patch[`${doc.idColumn}_filename`] = filename;
        patch[`${doc.idColumn}_file`] = filename;
        patch[doc.idColumn] = filename;
        patch[`anexo_${doc.idColumn}${idSuffix}`] = filename;
        patch[`anexo_${doc.idColumn}_filename`] = filename;
        patch[`anexo_${doc.idColumn}`] = filename;
      }
    }
    if (originalname) {
      patch[`${doc.id}${nomeSuffix}`] = originalname; // anexo_*_nome
      patch[`${doc.id}_originalname`] = originalname;
      patch[`anexo_${doc.id}${nomeSuffix}`] = originalname;
      patch[`anexo_${doc.id}_originalname`] = originalname;

      // Variações com `idColumn` para o rótulo também.
      if (doc.idColumn && doc.idColumn !== doc.id) {
        patch[`${doc.idColumn}${nomeSuffix}`] = originalname;
        patch[`${doc.idColumn}_originalname`] = originalname;
        patch[`anexo_${doc.idColumn}${nomeSuffix}`] = originalname;
        patch[`anexo_${doc.idColumn}_originalname`] = originalname;
      }
    }
  });
  return patch;
}

/**
 * Alias mantido para compatibilidade com usos antigos.
 */
export const getAnexosObrigatoriosIds = (schema, isStudent) =>
  buildAnexosObrigatoriosIds({ schema, isEstudante: isStudent });

/**
 * Extrai, da resposta 400 do backend, dois arrays já normalizados:
 *   - `anexosObrigatorios`: [{ id, label }] pronto para popular o
 *     `setAnexosObrigatoriosDestacar` e para preencher o modal de
 *     campos faltantes.
 *   - `camposFaltando`: string[] dos campos faltantes (sem objeto).
 *
 * Aceita tanto `string[]` quanto `Array<{ campo, id, rotulo,
 * label, nome }>` (porque o backend historicamente variou o formato).
 */
export function parseAnexosObrigatoriosFromError(errorResponse) {
  const result = { anexosObrigatorios: [], camposFaltando: [] };
  if (!errorResponse) return result;

  const arr = errorResponse.anexos_obrigatorios;
  if (Array.isArray(arr)) {
    result.anexosObrigatorios = arr
      .map((item) => {
        if (item == null) return null;
        if (typeof item === "string") {
          return { id: item, label: item };
        }
        if (typeof item === "object") {
          const id = item.id || item.campo;
          const label =
            item.label || item.rotulo || item.nome || item.campo || id;
          if (!id) return null;
          return { id, label };
        }
        return null;
      })
      .filter(Boolean);
  }

  const cf = errorResponse.campos_faltando;
  if (Array.isArray(cf)) {
    result.camposFaltando = cf
      .map((item) => {
        if (item == null) return null;
        if (typeof item === "string") return item;
        if (typeof item === "object") {
          return item.id || item.campo || item.label || item.rotulo;
        }
        return null;
      })
      .filter(Boolean);
  }

  return result;
}

/**
 * Alias legado (mantido por compatibilidade): extrai apenas os IDs a
 * destacar, sem label.
 */
export function parseAnexosObrigatoriosFromResponse(payload) {
  const { anexosObrigatorios } = parseAnexosObrigatoriosFromError(payload);
  return anexosObrigatorios.map((a) => a.id);
}

/**
 * Monta os "items" no formato esperado pelo MissingFieldsAlert,
 * já com label legível (rótulo do schema, se fornecido).
 *
 *   items: [{ id, label }]
 */
export function buildMissingItemsFromAnexos(ids, schema = []) {
  if (!Array.isArray(ids)) return [];
  const byId = new Map(
    (Array.isArray(schema) ? schema : []).map((d) => [d.id, d]),
  );
  return ids.map((id) => {
    const doc = byId.get(id);
    return {
      id,
      label: doc?.label || id,
    };
  });
}

/**
 * Cria a entrada de `anexosFiles` para um anexo já persistido no banco.
 *
 * O componente `DocumentUploadForm` considera "Anexado" quando
 * `filesById[doc.id]` é truthy e aceita tanto um `File` quanto um
 * objeto com `filename`. Sem essa hidratação, ao carregar um registro
 * existente os cards aparecem como "Pendente" mesmo com o path salvo.
 */
export function buildAnexoFileEntryPersisted({ path, originalname, name }) {
  if (!path) return null;
  const filename = String(path);
  const original =
    originalname ||
    name ||
    (filename.includes("_") ? filename.split("_").slice(1).join("_") : filename);
  return {
    filename,
    originalname: original,
    name: original,
    persisted: true,
  };
}

/**
 * Hidrata `anexosFiles` a partir do payload de GET (TCA/TcaEmpregador/TCE).
 *
 * Tenta, para cada `id` do schema, os seguintes campos no payload (na
 * ordem): `<id>_path`, `<id>_filename`, `<id>` e a forma com prefixo
 * `anexo_<id>_path`. Quando encontra um valor, monta a entrada via
 * `buildAnexoFileEntryPersisted` e mescla no `prev`.
 *
 * Não sobrescreve entradas já presentes (ex.: `File` recém-anexado
 * ainda não persistido) — só preenche chaves ausentes/vazias.
 *
 * @param {Object} params
 * @param {Array} params.schema      Lista de docs do schema (id, label, ...).
 * @param {Object} params.payload    Resposta do GET do registro.
 * @param {Object} [params.prev]     Estado atual de `anexosFiles` (opcional).
 * @returns {Object} Próximo estado de `anexosFiles` (novo objeto).
 */
export function hydrateAnexosFilesFromPayload({ schema, payload, prev } = {}) {
  const result = { ...(prev || {}) };
  if (!Array.isArray(schema) || !payload || typeof payload !== "object") {
    return result;
  }

  schema.forEach((doc) => {
    if (!doc || !doc.id) return;
    const existing = result[doc.id];
    // Não sobrescreve se já houver algo válido (File ou filename).
    if (
      existing &&
      (existing instanceof File ||
        existing.filename ||
        existing.originalname ||
        existing.name)
    ) {
      return;
    }
    const candidates = [
      `${doc.id}_path`,
      `${doc.id}_filename`,
      `${doc.id}`,
      `anexo_${doc.id}_path`,
      `anexo_${doc.id}`,
      // === Variações com `idColumn` (coluna real do banco) ===
      // Ex.: doc.id = "carga_horaria", doc.idColumn =
      // "declaracao_carga_horaria" -> busca
      // "anexo_declaracao_carga_horaria_path" antes de desistir.
      doc.idColumn && doc.idColumn !== doc.id
        ? [
            `${doc.idColumn}_path`,
            `${doc.idColumn}_filename`,
            `${doc.idColumn}`,
            `anexo_${doc.idColumn}_path`,
            `anexo_${doc.idColumn}`,
          ]
        : [],
    ].flat();
    const pathKey = candidates.find((k) =>
      [null, undefined, ""].every(
        (bad) => payload[k] !== bad && payload[k] !== "",
      ) && payload[k] != null,
    );
    const path = pathKey ? payload[pathKey] : null;
    if (!path) return;

    const nomeCandidates = [
      `${doc.id}_nome`,
      `${doc.id}_originalname`,
      `${doc.id}_name`,
      `anexo_${doc.id}_nome`,
      `anexo_${doc.id}_originalname`,
      `anexo_${doc.id}_name`,
      // Variações com `idColumn` para o nome também.
      doc.idColumn && doc.idColumn !== doc.id
        ? [
            `${doc.idColumn}_nome`,
            `${doc.idColumn}_originalname`,
            `${doc.idColumn}_name`,
            `anexo_${doc.idColumn}_nome`,
            `anexo_${doc.idColumn}_originalname`,
            `anexo_${doc.idColumn}_name`,
          ]
        : [],
    ].flat();
    const nomeKey = nomeCandidates.find(
      (k) => payload[k] != null && payload[k] !== "",
    );
    const nome = nomeKey ? payload[nomeKey] : null;

    const entry = buildAnexoFileEntryPersisted({
      path,
      originalname: nome,
    });
    if (entry) result[doc.id] = entry;
  });

  return result;
}
